import { useCallback, useEffect, useState } from "react";
import { MessagesSquare, Plus } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { Button } from "../ui/button";
import { Textarea } from "../ui/input";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CHECK_INS } from "../../constants/api";

/**
 * Mid-cycle check-ins between an employee and their manager.
 *
 * One row holds both halves of a conversation, and each side writes only its
 * own: the employee owns the note, the manager owns the comment. That is
 * enforced on the server too — this just avoids offering a box that will be
 * rejected.
 *
 * `role` is "employee" or "rm"; anything else (HR) gets a read-only view,
 * because a check-in records a conversation HR was not part of.
 */
const CheckInPanel = ({ appraisalId, goals = [], role, locked }) => {
    const { fetch, create, update } = useApiMethods();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [form, setForm] = useState({ note: "", goal_id: null, check_in_date: dayjs().format("YYYY-MM-DD") });

    const isEmployee = role === "employee";
    const isRM = role === "rm";
    const canWrite = (isEmployee || isRM) && !locked;
    const myField = isEmployee ? "employee_note" : "rm_comment";

    const load = useCallback(async () => {
        if (!appraisalId) return;
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_CHECK_INS, { appraisal_id: appraisalId });
            setRows(res?.data || []);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.note.trim()) {
            ToastMsg("error", "Write a note before saving");
            return;
        }
        setSaving(true);
        try {
            const res = await create(APPRAISAL_CHECK_INS, {
                appraisal_id: appraisalId,
                goal_id: form.goal_id || null,
                check_in_date: form.check_in_date,
                [myField]: form.note.trim(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "Check-in saved");
                setAdding(false);
                setForm({ note: "", goal_id: null, check_in_date: dayjs().format("YYYY-MM-DD") });
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not save the check-in");
            }
        } catch (_) {
            ToastMsg("error", "Could not save the check-in");
        } finally {
            setSaving(false);
        }
    };

    const saveReply = async (row) => {
        if (!replyText.trim()) return;
        setSaving(true);
        try {
            const res = await update(APPRAISAL_CHECK_INS, {
                id: row.id,
                [myField]: replyText.trim(),
            });
            if (res?.status === "success") {
                setReplyTo(null);
                setReplyText("");
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not save");
            }
        } catch (_) {
            ToastMsg("error", "Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <h3 className="text-base font-semibold flex items-center gap-2">
                    <MessagesSquare className="w-4 h-4" /> Check-ins
                </h3>
                {canWrite && !adding && (
                    <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add check-in
                    </Button>
                )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Notes recorded during the cycle, so the review reflects what actually
                happened rather than only the last few weeks.
            </p>

            {adding && (
                <div className="border border-border rounded-lg p-3 mb-3 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                            <label className="block text-xs font-medium mb-1">Date</label>
                            <DatePicker
                                value={form.check_in_date ? dayjs(form.check_in_date) : null}
                                onChange={(d) => setForm({ ...form, check_in_date: (d || dayjs()).format("YYYY-MM-DD") })}
                                format="DD-MM-YYYY"
                                className="w-full h-9"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">Against a goal (optional)</label>
                            <Select
                                allowClear
                                placeholder="General check-in"
                                value={form.goal_id}
                                onChange={(v) => setForm({ ...form, goal_id: v ?? null })}
                                options={goals.map((g) => ({ value: g.id, label: g.kpi_name }))}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <Textarea
                        rows={3}
                        autoFocus
                        placeholder={isEmployee
                            ? "What have you been working on against this goal?"
                            : "What did you discuss, and what did you agree?"}
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                    />
                    <div className="flex gap-2">
                        <Button size="sm" variant="brand" disabled={saving || !form.note.trim()} onClick={save}>
                            Save check-in
                        </Button>
                        <Button size="sm" variant="ghost" disabled={saving}
                            onClick={() => { setAdding(false); setForm({ note: "", goal_id: null, check_in_date: dayjs().format("YYYY-MM-DD") }); }}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-6 text-muted-foreground text-sm">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                    No check-ins recorded for this cycle yet.
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => {
                        const mineIsEmpty = !r[myField];
                        return (
                            <div key={r.id} className="border-l-2 border-border pl-3">
                                <div className="text-xs text-muted-foreground">
                                    {r.check_in_date ? dayjs(r.check_in_date).format("DD-MM-YYYY") : "—"}
                                    {r.goal_name ? ` · ${r.goal_name}` : " · General"}
                                </div>
                                {r.employee_note && (
                                    <p className="text-sm mt-1">
                                        <span className="font-medium">Employee: </span>{r.employee_note}
                                    </p>
                                )}
                                {r.rm_comment && (
                                    <p className="text-sm mt-1">
                                        <span className="font-medium">Manager: </span>{r.rm_comment}
                                    </p>
                                )}

                                {/* Each side may fill in its own half of a row the
                                    other opened, which is what turns a note into a
                                    conversation. */}
                                {canWrite && mineIsEmpty && (
                                    replyTo === r.id ? (
                                        <div className="mt-2 space-y-2">
                                            <Textarea
                                                rows={2}
                                                autoFocus
                                                placeholder={isEmployee ? "Add your note" : "Add your comment"}
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                            />
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="brand" disabled={saving || !replyText.trim()}
                                                    onClick={() => saveReply(r)}>
                                                    Save
                                                </Button>
                                                <Button size="sm" variant="ghost" disabled={saving}
                                                    onClick={() => { setReplyTo(null); setReplyText(""); }}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button type="button"
                                            className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline"
                                            onClick={() => { setReplyTo(r.id); setReplyText(""); }}>
                                            {isEmployee ? "Add your note" : "Add your comment"}
                                        </button>
                                    )
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CheckInPanel;
